import {
  applyStyle,
  type CopiedStyle,
  canApplyStyle,
  canCopyStyle,
  extractStyle,
  styleTypeLabel,
} from "@cva/shared";
import { toast } from "sonner";
import { create } from "zustand";
import { isMac } from "./ShortcutsDialog";
import type { EditorStore } from "./store";

/** „Format übertragen“: the copied style lives for the session (all documents). */
export const useStyleClipboard = create<{ style: CopiedStyle | null }>(() => ({ style: null }));

export function canCopySelectedStyle(store: EditorStore): boolean {
  const state = store.getState();
  if (state.selection.length !== 1) return false;
  return canCopyStyle(state.doc.elements[state.selection[0]!]);
}

/** Copies the style of the one selected element (⌥⌘C). */
export function copyStyle(store: EditorStore): void {
  const state = store.getState();
  if (state.selection.length !== 1) {
    toast.error("Wähle genau ein Element, dessen Format du übertragen möchtest");
    return;
  }
  const element = state.doc.elements[state.selection[0]!];
  const style = element ? extractStyle(element) : null;
  if (!style) {
    toast.error("Von diesem Element kann kein Format übertragen werden");
    return;
  }
  useStyleClipboard.setState({ style });
  toast.success(`Format kopiert – wähle Elemente und drücke ${isMac() ? "⌥⌘V" : "Strg+Alt+V"}`);
}

/** How many selected elements accept the copied style. */
export function pasteStyleTargets(store: EditorStore): number {
  const style = useStyleClipboard.getState().style;
  if (!style) return 0;
  const state = store.getState();
  return state.selection.filter((id) => canApplyStyle(style, state.doc.elements[id])).length;
}

/** Applies the copied style to the selected elements of the same type (⌥⌘V). One undo step. */
export function pasteStyle(store: EditorStore): void {
  const style = useStyleClipboard.getState().style;
  if (!style) {
    toast.error(`Noch kein Format kopiert (${isMac() ? "⌥⌘C" : "Strg+Alt+C"})`);
    return;
  }
  const state = store.getState();
  if (state.selection.length === 0) {
    toast.error("Wähle zuerst die Elemente, die das Format übernehmen sollen");
    return;
  }
  const targets = state.selection.filter((id) => canApplyStyle(style, state.doc.elements[id]));
  if (targets.length === 0) {
    toast.error(`Das kopierte Format passt nur auf ${styleTypeLabel(style.type)}`);
    return;
  }
  if (state.editingTextId && targets.includes(state.editingTextId)) state.setEditingText(null);
  store.getState().update((draft) => {
    for (const id of targets) {
      const element = draft.elements[id];
      if (element) applyStyle(element, style);
    }
  });
  const skipped = state.selection.length - targets.length;
  if (skipped > 0) {
    toast(`Format auf ${targets.length} von ${state.selection.length} Elementen übertragen`, {
      description: `Es passt nur auf ${styleTypeLabel(style.type)}.`,
    });
  }
}
